import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useLazyGetDefaultProfileQuery,
  useUpdateAiOperatorConfigMutation,
} from '@app/api/AiOperatorConfig';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type {
  FormValues,
  UseAiSettingsTabProps,
} from '@app/pages/Users/EditUser/components/AiSettingsTab/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { BaseOptionType } from 'antd/es/select';
import type { Dayjs } from 'dayjs';

export const useAiSettingsTab = ({
  data,
  allBranches,
}: UseAiSettingsTabProps) => {
  const [selectedBranches, setSelectedBranches] = useState<BaseOptionType[]>(
    []
  );

  const [form] = useForm();

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const { showNotification } = useNotification();

  const [updateAiOperatorConfig, { isLoading: isUpdating }] =
    useUpdateAiOperatorConfigMutation();
  const [getDefaultProfile] = useLazyGetDefaultProfileQuery();

  const [searchParams] = useSearchParams();

  const aiUserId = useMemo(
    () => +(searchParams.get('id') || 0),
    [searchParams]
  );

  useEffect(() => {
    if (data) {
      const endDialogTime = dayjs()
        .hour(data.EndDialogHours ?? 0)
        .minute(data.EndDialogMinutes ?? 0)
        .second(data.EndDialogSeconds ?? 0);

      const groupingTime = dayjs()
        .minute(data.GroupingMinutes ?? 0)
        .second(data.GroupingSeconds ?? 0);

      const branches = (data.BranchIds ?? [])
        .map((id) => allBranches.find((branch) => branch.value === id))
        .filter(Boolean) as BaseOptionType[];

      setSelectedBranches(branches);
      form.setFieldsValue({
        EndDialogDays: data.EndDialogDays?.toString() ?? '0',
        EndDialogTime: endDialogTime,
        GroupingTime: groupingTime,
        OperatorProfile: data.OperatorProfile,
        LlmProvider: data.LlmProvider,
        LlmModel: data.LlmModel,
        MaxSentences: data.MaxSentences,
        Tone: data.Tone,
        MandatoryCTA: data.MandatoryCTA,
        MessageTemplatesJson: data.MessageTemplatesJson,
      });
    }
  }, [allBranches, data, form]);

  const handleFinishForm = useCallback(
    async (values: FormValues) => {
      try {
        if (!selectedBranches.length) {
          showNotification('Пожалуйста, выберите хотя бы один филиал');
          return;
        }
        const {
          EndDialogDays,
          EndDialogTime,
          GroupingTime,
          OperatorProfile,
          LlmProvider,
          LlmModel,
          MaxSentences,
          Tone,
          MandatoryCTA,
          MessageTemplatesJson,
        } = values;

        const requestData = {
          EndDialogDays: Number(EndDialogDays),
          EndDialogHours: EndDialogTime.hour(),
          EndDialogMinutes: EndDialogTime.minute(),
          EndDialogSeconds: EndDialogTime.second(),
          GroupingMinutes: GroupingTime.minute(),
          GroupingSeconds: GroupingTime.second(),
          OperatorProfile,
          LlmProvider,
          LlmModel,
          MaxSentences,
          Tone,
          MandatoryCTA,
          MessageTemplatesJson,
          // GenericDialogV2Mode фронт не редактирует — round-trip загруженного
          // значения, чтобы сохранение не затирало его на бэке.
          GenericDialogV2Mode: data?.GenericDialogV2Mode ?? null,
          BranchIds: selectedBranches.map((item) => Number(item.value)),
        };

        await updateAiOperatorConfig({
          ActiveBranchId,
          userId: aiUserId,
          data: requestData,
        }).unwrap();

        showNotification('Данные сохранены успешно', SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      ActiveBranchId,
      aiUserId,
      data,
      selectedBranches,
      showNotification,
      updateAiOperatorConfig,
    ]
  );

  const handleGetDefaultProfile = useCallback(async () => {
    try {
      const data = await getDefaultProfile({
        ActiveBranchId,
      }).unwrap();

      form.setFieldValue('OperatorProfile', data);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [ActiveBranchId, form, getDefaultProfile, showNotification]);

  const validateJson = (_: unknown, value: string) => {
    if (!value || !value.trim()) {
      return Promise.resolve();
    }

    try {
      JSON.parse(value);
      return Promise.resolve();
    } catch {
      return showNotification(handleCatchError('Невалидный JSON'));
    }
  };

  const disabledTime = (current?: Dayjs) => {
    if (!current) return {};

    const selectedMin = current.minute();

    return {
      disabledHours: () => [...Array(24).keys()].slice(1),

      disabledMinutes: () => [...Array(60).keys()].slice(11), // ← ключевое изменение

      disabledSeconds: (_: number, min: number) =>
        min === 10 ? [...Array(60).keys()].slice(1) : [],

      ...(selectedMin > 10
        ? {
            disabledHours: () => [...Array(24).keys()],
            disabledMinutes: () => [...Array(60).keys()],
            disabledSeconds: () => [...Array(60).keys()],
          }
        : {}),
    };
  };

  const handleTimeChange = (time: Dayjs | null) => {
    if (!time) {
      const defaultTime = dayjs().hour(0).minute(0).second(0).millisecond(0);
      form.setFieldsValue({ GroupingTime: defaultTime });
      return;
    }

    const m = time.minute();
    const s = time.second();

    if (m > 10 || (m === 10 && s > 0)) {
      const corrected = dayjs().hour(0).minute(10).second(0).millisecond(0);
      form.setFieldsValue({ GroupingTime: corrected });
    }
  };

  return {
    form,
    handleFinishForm,
    isUpdating,
    handleGetDefaultProfile,
    validateJson,
    disabledTime,
    handleTimeChange,
    selectedBranches,
    setSelectedBranches,
  };
};
